import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../services/api";
import { format, addDays, subDays, parseISO } from "date-fns";
//import "./BusinessCashInPage.css";

const BusinessCashInPage = () => {
  const { id: businessId } = useParams();
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [entryData, setEntryData] = useState({});
  const [cashAdvanceData, setCashAdvanceData] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tempEntryData, setTempEntryData] = useState({});
  const [tempCashAdvanceData, setTempCashAdvanceData] = useState({});

  const [showAddCat, setShowAddCat] = useState(false);
  const [showEditCat, setShowEditCat] = useState(false);

  const [newCat, setNewCat] = useState({ name: "", category_type: "" });
  const [editCat, setEditCat] = useState({ id: "", name: "", category_type: "" });

   const CATEGORY_TYPES = [
   { value: "income", label: "income" },
   { value: "non-sale", label: "non-sale income" }, // display label only
   { value: "payment", label: "payment" },
   { value: "paidouts", label: "paidouts" },
   { value: "refunds", label: "refunds" },
 ];
  const RESERVED = new Set(["Open Cash", "Close Cash", "Required Cash", "Cash Advance"]);

  //const navigate = useNavigate();

  const daysToShow = [-3, -2, -1, 0, 1, 2, 3].map(offset =>
    format(addDays(currentDate, offset), "yyyy-MM-dd")
  );

  // ----- LOADERS (biz- endpoints) -----
  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get(`/biz-cash-register-categories`, {
        params: { business_id: businessId },
      });
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      alert("Failed to load categories");
    }
  }, [businessId]);

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await api.get(`/biz-load-employees`, {
        params: { business_id: businessId },
      });
      setEmployees(data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      alert("Failed to load employees");
    }
  }, [businessId]);

  const loadEntries = useCallback(async () => {
    try {
      const startDate = format(addDays(currentDate, -3), "yyyy-MM-dd");
      const endDate = format(addDays(currentDate, 3), "yyyy-MM-dd");

      const { data } = await api.get("/biz-cash-register-entries", {
        params: { business_id: businessId, start_date: startDate, end_date: endDate },
      });

      const newEntryData = {};
      const newCashAdvanceData = {};

      data.forEach(entry => {
        const dateKey = format(new Date(entry.entry_date), "yyyy-MM-dd");
        const key = `${entry.category_id}_${dateKey}`;

        if (entry.category_name === "Cash Advance") {
          if (!newCashAdvanceData[dateKey]) newCashAdvanceData[dateKey] = [];
          newCashAdvanceData[dateKey].push({
            id: entry.id,
            employee_id: entry.employee_id,
            amount: entry.amount,
            employee_name: entry.employee_name,
          });
        } else {
          newEntryData[key] = { id: entry.id, amount: entry.amount };
        }
      });

      setEntryData(newEntryData);
      setCashAdvanceData(newCashAdvanceData);
    } catch (err) {
      console.error("Error loading entries:", err);
      alert("Failed to load saved entries");
    }
  }, [businessId, currentDate]);

  useEffect(() => {
    loadCategories();
    loadEmployees();
    loadEntries();
  }, [loadCategories, loadEmployees, loadEntries]);

  // ----- HELPERS -----
  const handleInputChange = (categoryId, date, value) => {
    const key = `${categoryId}_${date}`;
    setTempEntryData(prev => ({ ...prev, [key]: value }));
  };

  const handleCashAdvanceChange = (date, index, field, value) => {
    setTempCashAdvanceData(prev => {
      const updated = { ...prev };
      if (!updated[date]) updated[date] = [];
      if (!updated[date][index]) updated[date][index] = {};
      updated[date][index][field] = value;
      return updated;
    });
  };

  const addCashAdvanceRow = (date) => {
    setTempCashAdvanceData(prev => {
      const updated = { ...prev };
      if (!updated[date]) updated[date] = [];
      updated[date].push({ employee_id: "", amount: "" });
      return updated;
    });
  };

  const removeCashAdvanceRow = (date, index) => {
    setTempCashAdvanceData(prev => {
      const updated = { ...prev };
      if (!updated[date]) return updated;
      updated[date] = updated[date].filter((_, i) => i !== index);
      return updated;
    });
  };

  function calculateRequiredCash(entryDataByCat, cashAdvanceTotal, categories) {
    const findCatByName = (name) => categories.find((c) => c.name === name);
    const getAmount = (name) => {
      const cat = findCatByName(name);
      return cat ? parseFloat(entryDataByCat[cat.id] || 0) : 0;
    };

    const openAmount = getAmount("Open Cash");
    const closeAmount = getAmount("Close Cash");

    const sumByType = (type) =>
      categories
        .filter((cat) => cat.category_type === type && cat.name !== "Cash Advance")
        .reduce((sum, cat) => sum + parseFloat(entryDataByCat[cat.id] || 0), 0);

    
    const incomeTotal = sumByType("income") + sumByType("non-sale");
    const paymentTotal = sumByType("payment");
    const refundTotal = sumByType("refunds");
    const paidoutsTotal = sumByType("paidouts") + cashAdvanceTotal;

    return (openAmount - closeAmount) + incomeTotal - paymentTotal - refundTotal - paidoutsTotal;
  }

  const nameExists = (name, exceptId = null) =>
    categories.some(
      (c) => c.name.toLowerCase() === name.trim().toLowerCase() && c.id !== exceptId
    );

  const cannotTouch = (cat) =>
    !cat || cat.category_type === "till" || RESERVED.has(cat.name);

  const editableCategories = categories.filter((c) => !cannotTouch(c));

  const resetAddCat = () => setNewCat({ name: "", category_type: "" });
  const resetEditCat = () => setEditCat({ id: "", name: "", category_type: "" });

  // ----- CATEGORY HANDLERS (biz- endpoints) -----
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCat.name.trim() || !newCat.category_type) {
      alert("Enter name and select type.");
      return;
    }
    if (RESERVED.has(newCat.name.trim())) {
      alert("That name is reserved.");
      return;
    }
    if (nameExists(newCat.name)) {
      alert("Category name already exists.");
      return;
    }
    try {
      await api.post("/biz-cash-register-categories", {
        name: newCat.name.trim(),
        category_type: newCat.category_type,
        business_id: businessId,
      });
      await loadCategories();
      resetAddCat();
      setShowAddCat(false);
    } catch (err) {
      console.error("Add category error:", err);
      alert("Failed to add category");
    }
  };

  const handleEditCatSelect = (idStr) => {
    const id = parseInt(idStr, 10);
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    if (cannotTouch(cat)) {
      alert("This category can’t be edited.");
      return;
    }
    setEditCat({ id: cat.id, name: cat.name, category_type: cat.category_type });
  };

  const handleSaveEditedCategory = async (e) => {
    e.preventDefault();
    if (!editCat.id || !editCat.name.trim() || !editCat.category_type) {
      alert("Select a category and fill all fields.");
      return;
    }
    const original = categories.find((c) => c.id === editCat.id);
    if (cannotTouch(original)) {
      alert("This category can’t be edited.");
      return;
    }
    if (nameExists(editCat.name, editCat.id)) {
      alert("Another category with this name already exists.");
      return;
    }
    try {
      await api.put(`/biz-cash-register-categories/${editCat.id}`, {
        name: editCat.name.trim(),
        category_type: editCat.category_type,
      });
      await loadCategories();
      resetEditCat();
      setShowEditCat(false);
    } catch (err) {
      console.error("Update category error:", err);
      alert("Failed to update category");
    }
  };

  const handleDisableCategory = async () => {
    if (!editCat.id) {
      alert("Select a category first.");
      return;
    }
    const cat = categories.find((c) => c.id === editCat.id);
    if (cannotTouch(cat)) {
      alert("This category can’t be disabled.");
      return;
    }
    if (!window.confirm(`Disable "${cat.name}"?`)) return;

    try {
      await api.put(`/biz-cash-register-categories/${editCat.id}/disable`);
      await loadCategories();
      resetEditCat();
      setShowEditCat(false);
    } catch (err) {
      console.error("Disable category error:", err);
      alert("Failed to disable category");
    }
  };

  // ----- ENTRIES (biz- endpoints) -----
  const handleNewEntriesForDate = async (date) => {
    const entriesToInsert = [];

    // Manual entries
    Object.entries(tempEntryData).forEach(([key, value]) => {
      const [category_id, entryDate] = key.split("_");
      if (entryDate === date && value !== "") {
        entriesToInsert.push({
          category_id: parseInt(category_id),
          date,
          amount: parseFloat(value),
          notes: null,
        });
      }
    });

    // Cash advances
    const cashAdvanceCat = categories.find(c => c.name === "Cash Advance");
    (tempCashAdvanceData[date] || []).forEach(row => {
      if (row.employee_id && row.amount) {
        entriesToInsert.push({
          category_id: cashAdvanceCat?.id,
          date,
          employee_id: parseInt(row.employee_id),
          amount: parseFloat(row.amount),
          notes: null,
        });
      }
    });

    try {
      await api.post("/biz-cash-register-entries", {
        business_id: businessId,
        entries: entriesToInsert,
      });
      await loadEntries();
   setTempEntryData(prev => {
     const updated = { ...prev };
     Object.keys(updated).forEach((k) => {
       const [, d] = k.split("_");
       if (d === date) delete updated[k];
     });
     return updated;
   });
   setTempCashAdvanceData(prev => {
     const updated = { ...prev };
     delete updated[date];
     return updated;
   });
   
    } catch (err) {
      console.error("Insert error:", err);
      alert("Failed to insert entries");
    }
  };



  
  const handleEditEntriesForDate = async (date) => {
    const entriesToUpdate = [];
    const entriesToInsert = [];

    // 1) Manual
    Object.entries(tempEntryData).forEach(([key, value]) => {
      const [category_id, entryDate] = key.split("_");
      if (entryDate !== date) return;
      if (value === "") return;

      const parsedAmount = parseFloat(value);
      if (isNaN(parsedAmount)) return;

      const savedEntry = entryData[key];
      if (savedEntry?.id) {
        entriesToUpdate.push({ id: savedEntry.id, amount: parsedAmount, notes: null });
      } else {
        entriesToInsert.push({ category_id: parseInt(category_id), date, amount: parsedAmount, notes: null });
      }
    });

    // 2) Cash Advance
    const cashAdvanceCat = categories.find((c) => c.name === "Cash Advance");
    (tempCashAdvanceData[date] || []).forEach((row) => {
      if (!row.employee_id || !row.amount) return;
      const parsedAmount = parseFloat(row.amount);
      if (isNaN(parsedAmount)) return;

      if (row.id) {
        entriesToUpdate.push({
          id: row.id,
          employee_id: parseInt(row.employee_id),
          amount: parsedAmount,
          notes: null,
        });
      } else {
        entriesToInsert.push({
          category_id: cashAdvanceCat?.id,
          date,
          employee_id: parseInt(row.employee_id),
          amount: parsedAmount,
          notes: null,
        });
      }
    });

    // 3) Required Cash auto-calc
    const requiredCashCat = categories.find((c) => c.name === "Required Cash");
    let requiredCashUpdate = null;

    if (requiredCashCat) {
      const entryDataByCat = {};
      categories.forEach((cat) => {
        if (cat.name === "Required Cash") return;
        const key = `${cat.id}_${date}`;
        const tempVal = tempEntryData[key];
        const savedVal = entryData[key]?.amount;
        entryDataByCat[cat.id] =
          tempVal !== undefined && tempVal !== ""
            ? parseFloat(tempVal)
            : savedVal !== undefined
            ? parseFloat(savedVal)
            : 0;
      });

      const totalCashAdvance = [
        ...(cashAdvanceData[date] || []),
        ...(tempCashAdvanceData[date] || []),
      ].reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);

      const rcAmount = calculateRequiredCash(entryDataByCat, totalCashAdvance, categories);
      const savedRC = entryData[`${requiredCashCat.id}_${date}`];
      if (savedRC?.id) {
        requiredCashUpdate = {
          id: savedRC.id,
          amount: parseFloat(rcAmount.toFixed(2)),
          notes: "Auto-updated Required Cash",
        };
      }
    }

    // 4) Short-circuit if nothing to do
    if (entriesToUpdate.length === 0 && entriesToInsert.length === 0 && !requiredCashUpdate) {
      alert(`No editable entries found for ${date}`);
      return;
    }

    try {
      if (entriesToInsert.length) {
        await api.post("/biz-cash-register-entries/insert-and-update-req-cash", {
          business_id: businessId,
          entries: entriesToInsert,
          required_cash_update: requiredCashUpdate,
        });
      }

      if (entriesToUpdate.length && !entriesToInsert.length) {
        await api.put("/biz-cash-register-entries", {
          business_id: businessId,
          entries: entriesToUpdate.concat(requiredCashUpdate ? [requiredCashUpdate] : []),
        });
      }



      // Clear temp state for this date
      setTempEntryData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          const [, entryDate] = key.split("_");
          if (entryDate === date) delete updated[key];
        });
        return updated;
      });

      setTempCashAdvanceData((prev) => {
        const updated = { ...prev };
        delete updated[date];
        return updated;
      });

      await loadEntries();
    } catch (err) {
      console.error("Edit error:", err);
      alert("Failed to save entries for this date");
    }
  };

  // ----- RENDER -----
  const grouped = categories.reduce((acc, cat) => {
    if (!acc[cat.category_type]) acc[cat.category_type] = [];
    acc[cat.category_type].push(cat);
    return acc;
  }, {});

  if (grouped["till"]) {
    grouped["till"].sort((a, b) => {
      const order = ["Open Cash", "Close Cash"];
      return order.indexOf(a.name) - order.indexOf(b.name);
    });
  }

  const goLeft = () => setCurrentDate(prev => subDays(prev, 1));
  const goRight = () => setCurrentDate(prev => addDays(prev, 1));
  const cashAdvanceCat = categories.find(c => c.name === "Cash Advance");

  return (
    <div className="cash-entries-page">
      <h2>Cash Entries</h2>

      

      <div style={{ marginBottom: 10 }}>
        <button onClick={goLeft}>⬅️</button>
        <button onClick={goRight} style={{ marginLeft: 8 }}>➡️</button>
      </div>

      <div className="cash-in-cat">
        <button onClick={() => setShowAddCat(true)}>+ Add Category</button>
        <button onClick={() => setShowEditCat(true)}>✏️ Edit / Disable Categories</button>
      </div>

      <div className="entries-grid" style={{ overflowX: "auto" }}>
        <table className="cash-entry-table" border="1" cellPadding="8" style={{ borderCollapse: "collapse", minWidth: "100%" }}>
          <thead>
            <tr>
              <th>Category</th>
              {daysToShow.map(date => (
                <th key={date}>{format(parseISO(date), "MMM d")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {["till", "income", "non-sale", "payment", "paidouts", "refunds"].map(type =>
              grouped[type]?.map(cat => (
                cat.name === "Cash Advance" ? null : (
                  <tr key={cat.id}>
                    <td>{cat.name}</td>
                    {daysToShow.map(date => {
                      const savedEntry = entryData[`${cat.id}_${date}`];
                      const tempKey = `${cat.id}_${date}`;
                      return (
                        <td key={tempKey}>
                          {tempEntryData[tempKey] !== undefined ? (
                            <input
                              type="number"
                              value={tempEntryData[tempKey] ?? ""}
                              onChange={(e) => handleInputChange(cat.id, date, e.target.value)}
                              style={{ width: "70px" }}
                            />
                          ) : savedEntry ? (
                            <>
                              <span>${savedEntry.amount}</span>
                              <button
                                onClick={() =>
                                  setTempEntryData(prev => ({ ...prev, [tempKey]: savedEntry.amount }))
                                }
                                style={{ marginLeft: "4px", background: "none", border: "none", color: "blue" }}
                              >
                                ✏️
                              </button>
                            </>
                          ) : (
                            <input
                              type="number"
                              value=""
                              onChange={(e) => handleInputChange(cat.id, date, e.target.value)}
                              style={{ width: "80px" }}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )
              ))
            )}

            {cashAdvanceCat && (
              <tr>
                <td><strong>Cash Advance</strong></td>
                {daysToShow.map(date => {
                  const displayRows = tempCashAdvanceData[date] ?? cashAdvanceData[date] ?? [];
                  return (
                    <td key={`ca_${date}`}>
                      {displayRows.map((entry, idx) => (
                        <div key={idx} className="cash-in-advance" style={{ marginBottom: "6px", display: "flex", gap: "4px", alignItems: "center" }}>
                          {entry.employee_name ? (
                            <>
                              <span>{entry.employee_name}: ${entry.amount}</span>
                              <button
                                onClick={() => {
                                  setTempCashAdvanceData(prev => {
                                    const updated = { ...prev };
                                    if (!updated[date]) updated[date] = [];
                                    updated[date].push({
                                      id: entry.id,
                                      employee_id: entry.employee_id,
                                      amount: entry.amount,
                                    });
                                    return updated;
                                  });
                                  setCashAdvanceData(prev => {
                                    const updated = { ...prev };
                                    updated[date] = updated[date].filter((_, i) => i !== idx);
                                    return updated;
                                  });
                                }}
                                style={{ background: "none", border: "none", color: "blue", marginLeft: "4px" }}
                              >
                                ✏️
                              </button>
                            </>
                          ) : (
                            <>
                              <select
                                value={entry.employee_id || ""}
                                onChange={(e) => handleCashAdvanceChange(date, idx, "employee_id", e.target.value)}
                                style={{ flex: 1 }}
                              >
                                <option value="">Select Employee</option>
                                {employees.map(emp => (
                                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                placeholder="Amount"
                                value={entry.amount || ""}
                                onChange={(e) => handleCashAdvanceChange(date, idx, "amount", e.target.value)}
                                style={{ width: "80px" }}
                              />
                              <button onClick={() => removeCashAdvanceRow(date, idx)} className="remove-advance">−</button>
                            </>
                          )}
                        </div>
                      ))}
                      <button className="add-advance" onClick={() => addCashAdvanceRow(date)}>+ Add</button>
                    </td>
                  );
                })}
              </tr>
            )}

            <tr>
              <td><strong>Required Cash</strong></td>
              {daysToShow.map(date => {
                const openCashCat = categories.find(c => c.name === "Open Cash");
                const closeCashCat = categories.find(c => c.name === "Close Cash");

                const openAmount = parseFloat(entryData[`${openCashCat?.id}_${date}`]?.amount || 0);
                const closeAmount = parseFloat(entryData[`${closeCashCat?.id}_${date}`]?.amount || 0);

                const sumByType = (type) => {
                  return (grouped[type] || []).reduce((sum, cat) => {
                    if (cat.name === "Cash Advance") return sum;
                    const val = parseFloat(entryData[`${cat.id}_${date}`]?.amount || 0);
                    return sum + (isNaN(val) ? 0 : val);
                  }, 0);
                };

                const paidoutsTotal = (cashAdvanceData[date] || [])
                  .reduce((sum, row) => sum + parseFloat(row.amount || 0), 0)
                  + sumByType('paidouts');
                const incomeTotal = sumByType("income") + sumByType("non-sale");
                const paymentTotal = sumByType('payment');
                const refundTotal = sumByType('refunds');

                const requiredCash = (openAmount - closeAmount) + incomeTotal - paymentTotal - refundTotal - paidoutsTotal;

                return (
                  <td key={`required_cash_${date}`}>
                    <strong>${requiredCash.toFixed(2)}</strong>
                  </td>
                );
              })}
            </tr>

            <tr>
              <td></td>
              {daysToShow.map(date => (
                <td key={`submit_${date}`}>
                  <button
                    className="cash-in-save"
                    onClick={() => {
                      const isNewDay = !Object.keys(entryData).some(k => k.includes(date));
                      isNewDay ? handleNewEntriesForDate(date) : handleEditEntriesForDate(date);
                    }}
                  >
                    Save
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Add Category Modal */}
      {showAddCat && (
        <div className="cash-in-cat-pop">
          <div>
            <h3>Add Category</h3>
            <form onSubmit={handleAddCategory}>
              <div>
                <label>
                  Name{" "}
                  <input
                    type="text"
                    value={newCat.name}
                    onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))}
                  />
                </label>
              </div>
              <div>
                <label>
                  Type{" "}
                  <select
                    value={newCat.category_type}
                    onChange={(e) => setNewCat((s) => ({ ...s, category_type: e.target.value }))}
                  >
                    <option value="">-- Select --</option>
                    {CATEGORY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                     ))}
                  </select>
                </label>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="button" onClick={() => { resetAddCat(); setShowAddCat(false); }}>
                  Cancel
                </button>
                <button type="submit">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit/Disable Category Modal */}
      {showEditCat && (
        <div className="cash-in-edit-pop">
          <div>
            <h3>Edit / Disable Category</h3>
            <form onSubmit={handleSaveEditedCategory}>
              <div>
                <label>
                  Select{" "}
                  <select value={editCat.id} onChange={(e) => handleEditCatSelect(e.target.value)}>
                    <option value="">-- Choose --</option>
                    {editableCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name} ({c.category_type})</option>
                    ))}
                  </select>
                </label>
              </div>

              <div>
                <label>
                  Name{" "}
                  <input
                    type="text"
                    value={editCat.name}
                    onChange={(e) => setEditCat((s) => ({ ...s, name: e.target.value }))}
                  />
                </label>
              </div>

              <div>
                <label>
                  Type{" "}
                  <select
                    value={editCat.category_type}
                    onChange={(e) => setEditCat((s) => ({ ...s, category_type: e.target.value }))}
                  >
                    <option value="">-- Select --</option>
                    {CATEGORY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                  </select>
                </label>
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8, justifyContent: "space-between" }}>
                <button type="button" onClick={handleDisableCategory} style={{ color: "red" }}>
                  Disable
                </button>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={() => { resetEditCat(); setShowEditCat(false); }}>
                    Cancel
                  </button>
                  <button type="submit">Save</button>
                </div>
              </div>
            </form>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Note: “Open Cash”, “Close Cash”, “Required Cash”, “Cash Advance”, and any “till” type cannot be edited or disabled.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCashInPage;
